/**
 * Workspace embed: elevación por postMessage (auth-only).
 * Contrato: docs/CONTRATO-UDV-WORKSPACE-EMBED.md
 */
import {
  ENTITLEMENT_SOURCES,
  UDV_PLANS,
  WORKSPACE_ELEVATE_MESSAGE_TYPE,
  WORKSPACE_ELEVATE_MESSAGE_VERSION,
} from './constants'
import {
  parseEmbedFromSearchParams,
  parseHideFullscreenPromptFromSearchParams,
  parsePlanQueryParam,
} from './parsePlanQuery'
import { parseThemeQueryParam } from './chromeTheme'
import { isEmbeddedInIframe } from '../services/udvCopilotInsert'
import { fetchUdvElevation } from '../services/viewerTicket'

/** @type {string[]} */
const DEFAULT_WORKSPACE_EMBED_ORIGINS = [
  'https://plataforma.tanjousoft.com.ar',
  'https://dashboard.tanjousoft.com.ar',
  'https://plataforma.iddse.com.ar',
]

export const DEFAULT_WORKSPACE_EMBED_TIMEOUT_MS = 12000

/**
 * @returns {boolean}
 */
export function detectEmbeddedInIframe() {
  return isEmbeddedInIframe()
}

/**
 * @param {string|null|undefined} raw
 * @returns {string[]}
 */
function splitOriginCsv(raw) {
  if (raw == null || String(raw).trim() === '') return []
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * Orígenes exactos: env + defaults de build.
 * @returns {string[]}
 */
export function getWorkspaceEmbedAllowedOrigins() {
  /** @type {string[]} */
  const out = []
  const seen = Object.create(null)
  const add = (o) => {
    const v = o != null ? String(o).trim().replace(/\/+$/, '') : ''
    if (!v || seen[v]) return
    seen[v] = true
    out.push(v)
  }
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_PLATFORM_ORIGIN
    ) {
      add(process.env.REACT_APP_PLATFORM_ORIGIN)
    }
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_WORKSPACE_EMBED_ORIGINS
    ) {
      splitOriginCsv(process.env.REACT_APP_WORKSPACE_EMBED_ORIGINS).forEach(add)
    }
  } catch (e) {
    /* ignore */
  }
  DEFAULT_WORKSPACE_EMBED_ORIGINS.forEach(add)
  return out
}

/**
 * @param {string|null|undefined} origin
 * @param {string[]} [allowedExact]
 * @returns {boolean}
 */
export function isAllowedWorkspaceOrigin(origin, allowedExact) {
  if (origin == null || String(origin).trim() === '') return false
  const o = String(origin).trim().replace(/\/+$/, '')
  const list = allowedExact || getWorkspaceEmbedAllowedOrigins()
  if (list.indexOf(o) >= 0) return true
  try {
    const u = new URL(o)
    if (u.protocol !== 'https:') return false
    const h = String(u.hostname || '').toLowerCase()
    if (!h) return false
    if (h === 'portalpaciente.com.ar' || h.endsWith('.portalpaciente.com.ar')) {
      return true
    }
    if (h === 'tanjousoft.com.ar' || h.endsWith('.tanjousoft.com.ar')) {
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

/**
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   isEmbedded?: boolean,
 * }} [opts]
 * @returns {boolean}
 */
export function isWorkspaceEmbedContext(opts = {}) {
  const embedded =
    opts.isEmbedded !== undefined ? !!opts.isEmbedded : detectEmbeddedInIframe()
  if (!embedded) return false
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  if (!params) return false
  if (parseEmbedFromSearchParams(params) === 'workspace') return true
  const studyId = params.get('studyId')
  const pacs = params.get('pacs') || params.get('pacsId')
  const hideFs = parseHideFullscreenPromptFromSearchParams(params)
  const hasStudy =
    studyId != null &&
    String(studyId).trim() !== '' &&
    pacs != null &&
    String(pacs).trim() !== ''
  return hasStudy && hideFs
}

/**
 * @param {*} data
 * @returns {{
 *   t: string,
 *   elev: string,
 *   ticketApi: string,
 *   edge_id: string|null,
 * }|null}
 */
export function parseWorkspaceElevateMessage(data) {
  if (!data || typeof data !== 'object') return null
  if (data.type !== WORKSPACE_ELEVATE_MESSAGE_TYPE) return null
  // Plataforma antigua sin version: tratar como 1 (contrato actual)
  const verRaw = data.version
  const ver =
    verRaw == null || String(verRaw).trim() === ''
      ? WORKSPACE_ELEVATE_MESSAGE_VERSION
      : Number(verRaw)
  if (ver !== WORKSPACE_ELEVATE_MESSAGE_VERSION) return null
  const t = data.t != null ? String(data.t).trim() : ''
  const elev = data.elev != null ? String(data.elev).trim() : ''
  const ticketApi =
    data.ticketApi != null ? String(data.ticketApi).trim() : ''
  if (!t || !elev || !ticketApi) return null
  if (!/^https?:\/\//i.test(ticketApi)) return null
  const edgeRaw = data.edge_id != null ? data.edge_id : data.edgeId
  const edge_id =
    edgeRaw != null && String(edgeRaw).trim() !== ''
      ? String(edgeRaw).trim()
      : null
  return { t, elev, ticketApi, edge_id }
}

/**
 * @param {{
 *   timeoutMs?: number,
 *   allowedOrigins?: string[],
 *   addEventListener?: typeof window.addEventListener,
 *   removeEventListener?: typeof window.removeEventListener,
 * }} [opts]
 * @returns {Promise<{
 *   t: string,
 *   elev: string,
 *   ticketApi: string,
 *   edge_id: string|null,
 *   origin: string,
 * }>}
 */
export function waitForWorkspaceElevate(opts = {}) {
  const timeoutMs =
    opts.timeoutMs != null ? opts.timeoutMs : DEFAULT_WORKSPACE_EMBED_TIMEOUT_MS
  const allowed = opts.allowedOrigins || getWorkspaceEmbedAllowedOrigins()
  const add =
    opts.addEventListener ||
    (typeof window !== 'undefined'
      ? window.addEventListener.bind(window)
      : null)
  const remove =
    opts.removeEventListener ||
    (typeof window !== 'undefined'
      ? window.removeEventListener.bind(window)
      : null)

  return new Promise((resolve, reject) => {
    if (!add || !remove) {
      reject(new Error('no_window'))
      return
    }
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      remove('message', onMessage)
      reject(new Error('timeout'))
    }, timeoutMs)

    function onMessage(event) {
      if (settled) return
      if (!isAllowedWorkspaceOrigin(event.origin, allowed)) return
      const parsed = parseWorkspaceElevateMessage(event.data)
      if (!parsed) return
      settled = true
      clearTimeout(timer)
      remove('message', onMessage)
      resolve({ ...parsed, origin: event.origin })
    }

    add('message', onMessage)
  })
}

/**
 * @param {{
 *   t: string,
 *   elev: string,
 *   ticketApi: string,
 *   edge_id?: string|null,
 *   fetchImpl?: typeof fetch,
 * }} opts
 * @returns {Promise<{
 *   ok: boolean,
 *   entitlements?: object,
 *   plan?: string,
 *   shell?: string|null,
 *   reason?: string|null,
 * }>}
 */
export async function redeemWorkspaceElevate(opts) {
  const t = opts.t != null ? String(opts.t).trim() : ''
  const elev = opts.elev != null ? String(opts.elev).trim() : ''
  const ticketApi =
    opts.ticketApi != null ? String(opts.ticketApi).trim() : ''
  if (!t || !elev || !ticketApi) {
    return { ok: false, reason: 'missing_token' }
  }
  const redeemed = await fetchUdvElevation(t, {
    baseUrl: ticketApi,
    elevationToken: elev,
    fetchImpl: opts.fetchImpl,
  })
  if (!redeemed.ok || !redeemed.authorized || !redeemed.plan) {
    return {
      ok: false,
      reason: redeemed.reason || 'no_session',
    }
  }
  const plan = parsePlanQueryParam(redeemed.plan) || UDV_PLANS.LITE
  const shell =
    redeemed.shell != null && String(redeemed.shell).trim() !== ''
      ? String(redeemed.shell).trim()
      : null
  const theme = parseThemeQueryParam(redeemed.theme)
  const edgeId =
    (opts.edge_id != null && String(opts.edge_id).trim() !== ''
      ? String(opts.edge_id).trim()
      : null) ||
    (redeemed.edgeId != null && String(redeemed.edgeId).trim() !== ''
      ? String(redeemed.edgeId).trim()
      : null)
  return {
    ok: true,
    plan,
    shell,
    theme,
    entitlements: {
      plan,
      shell,
      theme,
      pacsId: null,
      udvToken: null,
      edgeId,
      clinicName: null,
      source: ENTITLEMENT_SOURCES.WORKSPACE_EMBED,
    },
  }
}
